import React from 'react';
import { useAtom } from 'jotai';
import { useQueryStates, parseAsInteger, parseAsString } from 'nuqs';

import { plansHydratedAtom, plansListParamsAtom } from '@/store';

export interface PlansSearchFields {
  is_active: string;
}

export function usePlansFilters() {
  const [params, setParams] = useAtom(plansListParamsAtom);
  const [, setHydrated] = useAtom(plansHydratedAtom);

  const [queryParams, setQueryParams] = useQueryStates({
    page: parseAsInteger.withDefault(1),
    per_page: parseAsInteger.withDefault(15),
    sort: parseAsString,
    'filter[is_active]': parseAsString
  });

  React.useEffect(() => {
    const filters: Record<string, string | number | boolean | undefined> = {};
    if (queryParams['filter[is_active]'] !== null && queryParams['filter[is_active]'] !== '') {
      const v = queryParams['filter[is_active]'];
      if (v === 'true' || v === '1') filters.is_active = true;
      else if (v === 'false' || v === '0') filters.is_active = false;
    }

    setParams((prev) => ({
      ...prev,
      page: queryParams.page,
      per_page: queryParams.per_page,
      sort: queryParams.sort || undefined,
      filters
    }));

    setHydrated(true);
  }, [
    queryParams.page,
    queryParams.per_page,
    queryParams.sort,
    queryParams['filter[is_active]'],
    setParams,
    setHydrated
  ]);

  const searchFields: PlansSearchFields = {
    is_active: params.filters.is_active === undefined ? '' : String(params.filters.is_active)
  };

  const setSearchFields = React.useCallback(
    (updater: (prev: PlansSearchFields) => PlansSearchFields) => {
      const next = updater(searchFields);
      setQueryParams({
        'filter[is_active]': next.is_active || null,
        page: 1
      });
    },
    [searchFields, setQueryParams]
  );

  const clearFilters = React.useCallback(() => {
    setQueryParams({
      'filter[is_active]': null,
      page: 1
    });
  }, [setQueryParams]);

  const handlePageChange = React.useCallback(
    (page: number) => {
      setQueryParams({ page });
    },
    [setQueryParams]
  );

  const sortParam = params.sort;
  const setSortParam = React.useCallback(
    (sortValue: string | null) => {
      setQueryParams({ sort: sortValue || null, page: 1 });
    },
    [setQueryParams]
  );

  const hasAnyFilter = Boolean(queryParams['filter[is_active]']);

  return {
    searchFields,
    setSearchFields,
    clearFilters,
    handlePageChange,
    hasAnyFilter,
    sortParam,
    setSortParam
  };
}
